import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDown, Mail } from "lucide-react";
import api from "@/lib/api";

const TYPES = [
  { v: "personale", l: "Debiti personali", min: 0.5, max: 0.7 },
  { v: "aziendale", l: "Debiti aziendali", min: 0.5, max: 0.7 },
  { v: "mutuo", l: "Mutuo / casa a rischio", min: 0.5, max: 0.7 },
];

const PRESETS = [25000, 50000, 100000, 250000, 500000];

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
const fmtShort = (n) => (n >= 1000 ? `${Math.round(n / 1000)}k €` : fmt(n));

export const SavingsCalculator = () => {
  const [amount, setAmount] = useState(100000);
  const [type, setType] = useState(TYPES[0]);
  const [lead, setLead] = useState({ name: "", email: "" });
  const [leadSending, setLeadSending] = useState(false);
  const [leadSent, setLeadSent] = useState(false);

  const est = useMemo(() => ({
    saveMin: Math.round(amount * type.min),
    saveMax: Math.round(amount * type.max),
    payMin: Math.round(amount * (1 - type.max)),
    payMax: Math.round(amount * (1 - type.min)),
  }), [amount, type]);

  const payPctMid = Math.round(((1 - type.max) + (1 - type.min)) / 2 * 100);

  const sendLead = async (e) => {
    e.preventDefault();
    setLeadSending(true);
    try {
      await api.post("/calculator-lead", { ...lead, amount, debt_type: type.v });
      setLeadSent(true);
      toast.success("Stima inviata! Controlla la tua email.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio");
    } finally { setLeadSending(false); }
  };

  return (
    <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="savings-calculator-section">
      <div className="container-editorial grid grid-cols-12 gap-8 md:gap-12 items-start">
        <div className="col-span-12 md:col-span-4">
          <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#2d6fc4]">— Calcolatore di risparmio</p>
          <h2 className="font-serif text-4xl md:text-6xl leading-[0.95] mt-4 text-[#2d6fc4]">
            Quanto potresti<br/><em className="italic text-[#2d6fc4]">risparmiare?</em>
          </h2>
          <p className="mt-6 text-lg text-[#333333] leading-relaxed max-w-md">
            Bastano due scelte: quanto devi e che tipo di debito è. Il resto lo calcoliamo noi, sulla base dei casi che abbiamo davvero chiuso.
          </p>
          <p className="mt-6 text-xs text-[#333333] max-w-md">
            * Stima basata sui nostri casi risolti: nella maggioranza dei casi la riduzione è tra il 50% e il 70%, con punte fino al 90% e oltre — come un mutuo residuo di 57.000 € chiuso a 2.500 €. Ogni pratica è valutata singolarmente.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="col-span-12 md:col-span-8 border border-[#E5E7EB] bg-white rounded-lg overflow-hidden"
          data-testid="savings-calculator-card"
        >
          {/* STEP 1 */}
          <div className="p-8 md:p-10 border-b border-[#E5E7EB]">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#1a4a8a] text-white font-mono text-xs shrink-0">1</span>
              <p className="font-medium text-[#2d6fc4]">Quanto devi in totale ai tuoi creditori?</p>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              {PRESETS.map(p => (
                <button key={p} type="button" onClick={() => setAmount(p)}
                  data-testid={`calc-preset-${p}`}
                  className={`px-4 py-2 rounded-full text-sm border ${amount === p ? "bg-[#1a4a8a] text-white border-[#0B1F3F]" : "border-[#E5E7EB] text-[#2d6fc4] hover:border-[#14417B]"}`}
                  style={{ transition: "background-color 200ms ease, border-color 200ms ease" }}>
                  {fmtShort(p)}
                </button>
              ))}
              <span className="text-xs text-[#333333] ml-1">oppure usa il cursore ↓</span>
            </div>
            <div className="mt-5 flex items-center gap-5">
              <input
                type="range" min={10000} max={500000} step={5000} value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                data-testid="calc-slider"
                className="flex-1 accent-[#14417B] cursor-pointer"
              />
              <p className="font-serif text-3xl md:text-4xl text-[#2d6fc4] w-40 text-right shrink-0" data-testid="calc-amount">{fmt(amount)}</p>
            </div>
          </div>

          {/* STEP 2 */}
          <div className="p-8 md:p-10 border-b border-[#E5E7EB]">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#1a4a8a] text-white font-mono text-xs shrink-0">2</span>
              <p className="font-medium text-[#2d6fc4]">Che tipo di debito è?</p>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {TYPES.map(t => (
                <button
                  key={t.v} type="button" onClick={() => setType(t)}
                  data-testid={`calc-type-${t.v}`}
                  className={`px-5 py-2.5 rounded-full text-sm border ${type.v === t.v ? "bg-[#1a4a8a] text-white border-[#0B1F3F]" : "border-[#E5E7EB] text-[#2d6fc4] hover:border-[#14417B]"}`}
                  style={{ transition: "background-color 200ms ease, border-color 200ms ease" }}
                >
                  {t.l}
                </button>
              ))}
            </div>
          </div>

          {/* RESULT */}
          <div className="p-8 md:p-10 bg-[#F8FAFC]">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#14417B] text-white font-mono text-xs shrink-0">3</span>
              <p className="font-medium text-[#2d6fc4]">Ecco cosa potrebbe succedere con il saldo e stralcio</p>
            </div>
            <p className="mt-2 ml-10 text-xs text-[#333333] italic" data-testid="calc-disclaimer-top">
              Valori puramente esemplificativi, basati sulla media dei nostri casi risolti: non costituiscono una promessa di risultato.
            </p>

            <div className="mt-6 space-y-4">
              <div>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-[#333333]">Oggi devi</span>
                  <span className="font-medium text-[#333333] line-through decoration-[#B91C1C]/60">{fmt(amount)}</span>
                </div>
                <div className="h-9 rounded-lg bg-[#E5E7EB] w-full" data-testid="calc-bar-debt"/>
              </div>

              <div className="flex items-center gap-2 text-[#2d6fc4]">
                <ArrowDown size={16}/>
                <span className="text-xs font-mono tracking-wider uppercase">dopo la trattativa</span>
              </div>

              <div>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-[#2d6fc4] font-medium">Pagheresti solo</span>
                  <span className="font-serif text-2xl text-[#2d6fc4]" data-testid="calc-payoff">{fmt(est.payMin)} – {fmt(est.payMax)}</span>
                </div>
                <motion.div
                  className="h-9 rounded-lg bg-[#1a4a8a] flex items-center justify-end pr-3"
                  animate={{ width: `${payPctMid}%` }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  data-testid="calc-bar-pay"
                >
                  <span className="text-white text-xs font-mono">~{payPctMid}%</span>
                </motion.div>
              </div>
            </div>

            <div className="mt-7 rounded-lg bg-[#14417B] text-white px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white">Il tuo risparmio stimato *</p>
              <p className="font-serif text-3xl md:text-4xl leading-none" data-testid="calc-saving">{fmt(est.saveMin)} – {fmt(est.saveMax)}</p>
            </div>
            <p className="mt-2 text-[11px] text-[#333333]" data-testid="calc-disclaimer-bottom">
              * Esempio indicativo: l'esito reale dipende da creditori, garanzie e documentazione. Ogni pratica è valutata singolarmente.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link to="/prenota" data-testid="calc-cta-booking" className="btn-cta inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm tracking-wide">
                Prenota una chiamata gratuita <ArrowUpRight size={15}/>
              </Link>
              <p className="text-xs text-[#333333]">Verifichiamo insieme se il tuo caso è trattabile.</p>
            </div>

            <div className="mt-8 border-t border-[#E5E7EB] pt-6">
              {leadSent ? (
                <p className="text-sm text-[#2d6fc4] font-medium" data-testid="calc-lead-success">
                  ✓ Stima inviata a {lead.email}. Controlla la casella (anche lo spam).
                </p>
              ) : (
                <form onSubmit={sendLead} data-testid="calc-lead-form">
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#2d6fc4] mb-3 flex items-center gap-2">
                    <Mail size={12}/> Ricevi questa stima via email
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input required minLength={2} placeholder="Nome" data-testid="calc-lead-name"
                      className="flex-1 bg-transparent border-b border-[#E5E7EB] py-2.5 text-sm focus:border-[#14417B] outline-none"
                      value={lead.name} onChange={e => setLead(l => ({ ...l, name: e.target.value }))}/>
                    <input required type="email" placeholder="La tua email" data-testid="calc-lead-email"
                      className="flex-1 bg-transparent border-b border-[#E5E7EB] py-2.5 text-sm focus:border-[#14417B] outline-none"
                      value={lead.email} onChange={e => setLead(l => ({ ...l, email: e.target.value }))}/>
                    <button type="submit" disabled={leadSending} data-testid="calc-lead-submit"
                      className="border border-[#0B1F3F]/25 hover:border-[#14417B] text-[#2d6fc4] px-5 py-2.5 rounded-lg text-sm disabled:opacity-50"
                      style={{ transition: "border-color 200ms ease" }}>
                      {leadSending ? "Invio…" : "Inviami la stima"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
